import { createFileRoute, redirect, useNavigate, useParams } from '@tanstack/react-router'

import { useAuthStore } from '@/shared/auth/stores/authStore'
import { isHiddenEntity } from '@/shared/constants/demo-data'
import { EntityDetailPage } from '@/systems/entities/features/entity-detail/components/EntityDetailPage'

// Search params persist tab + filter selections in the URL so the browser's
// back button restores them when the user returns from a detail page
// (response detail, site detail, etc.). Without this, every back-nav lands
// the user on the default tab — a stateless feel reported as a bug.
export type EntityDetailTab =
  | 'all_sites'
  | 'surveys'
  | 'issues'
  | 'form_templates'
  | 'officers'

export type EntityDetailPeriod =
  | 'this_month'
  | 'last_month'
  | 'last_3_months'
  | 'this_year'

export type SurveyTabType = 'one_time' | 'recurring'
export type SurveyStatusFilter = 'pending' | 'completed' | 'missed'

export interface EntityDetailSearch {
  tab?: EntityDetailTab
  period?: EntityDetailPeriod
  map?: boolean
  // Surveys-tab sub-view + paging/filter state, persisted in the URL so the
  // browser back button and refresh restore the exact survey view.
  //  - surveyType absent → the Recurring/One-time chooser
  //  - surveyType=recurring (+ no seriesId) → the recurring series list
  //  - surveyType=recurring + seriesId → that series' surveys
  //  - surveyType=one_time (+ no seriesId) → the one-time schedule list
  //  - surveyType=one_time + seriesId → that one-time schedule's surveys
  surveyType?: SurveyTabType
  seriesId?: string
  surveyStatus?: SurveyStatusFilter
  surveyPage?: number
}

const VALID_TABS: readonly EntityDetailTab[] = [
  'all_sites',
  'surveys',
  'issues',
  'form_templates',
  'officers',
]
const VALID_PERIODS: readonly EntityDetailPeriod[] = [
  'this_month',
  'last_month',
  'last_3_months',
  'this_year',
]

function asTab(v: unknown): EntityDetailTab | undefined {
  return typeof v === 'string' && (VALID_TABS as readonly string[]).includes(v)
    ? (v as EntityDetailTab)
    : undefined
}

function asPeriod(v: unknown): EntityDetailPeriod | undefined {
  return typeof v === 'string' && (VALID_PERIODS as readonly string[]).includes(v)
    ? (v as EntityDetailPeriod)
    : undefined
}

function asSurveyType(v: unknown): SurveyTabType | undefined {
  return v === 'one_time' || v === 'recurring' ? v : undefined
}

function asSurveyStatus(v: unknown): SurveyStatusFilter | undefined {
  return v === 'pending' || v === 'completed' || v === 'missed' ? v : undefined
}

function asPage(v: unknown): number | undefined {
  const n = Number(v)
  return Number.isFinite(n) && n >= 1 ? Math.floor(n) : undefined
}

export const Route = createFileRoute('/entities/$id')({
  beforeLoad: ({ params }) => {
    const { isAuthenticated } = useAuthStore.getState()
    if (!isAuthenticated) {
      throw redirect({ to: '/login' })
    }
    // Demo/hidden entities are suppressed everywhere in the web app — block
    // direct deep-links to their detail page too.
    if (isHiddenEntity(params.id)) {
      throw redirect({ to: '/entities' })
    }
  },
  validateSearch: (search: Record<string, unknown>): EntityDetailSearch => {
    // Build the object up; only include keys with valid values so
    // `exactOptionalPropertyTypes: true` doesn't reject `undefined` slots.
    const out: EntityDetailSearch = {}
    const tab = asTab(search['tab'])
    const period = asPeriod(search['period'])
    if (tab) {out.tab = tab}
    if (period) {out.period = period}
    if (search['map'] === true || search['map'] === 'true') {out.map = true}
    const surveyType = asSurveyType(search['surveyType'])
    const surveyStatus = asSurveyStatus(search['surveyStatus'])
    const surveyPage = asPage(search['surveyPage'])
    if (surveyType) {out.surveyType = surveyType}
    if (typeof search['seriesId'] === 'string' && search['seriesId']) {
      out.seriesId = search['seriesId']
    }
    if (surveyStatus) {out.surveyStatus = surveyStatus}
    if (surveyPage) {out.surveyPage = surveyPage}
    return out
  },
  component: EntityDetailRoute,
})

function EntityDetailRoute() {
  const { id } = useParams({ from: '/entities/$id' })
  const navigate = useNavigate()

  return (
    <EntityDetailPage
      entityId={id}
      entityName=""
      onBack={() => navigate({ to: '/entities' })}
    />
  )
}
